// WHAT: Utility functions for formatting data for display
// WHY:  Provides consistent formatting across Miracle Flow application
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: None
// RISKS:
//   - NOTE: Formatting may not match user locale preferences
//   - NOTE: Date formatting uses hardcoded locale ('en-US')

/**
 * WHAT:
 *   Utility functions for formatting various data types including durations,
 *   dates, times, file sizes, percentages, and session types.
 *
 * WHY:
 *   Ensures consistent formatting across the application.
 *
 * BEHAVIOR:
 *   - Formats durations in HH:MM:SS or MM:SS format
 *   - Formats relative time (time ago)
 *   - Formats dates and times
 *   - Formats file sizes with units
 *   - Formats percentages
 *   - Formats session types
 *
 * RISKS:
 *   - NOTE: Formatting may not match user locale preferences
 *   - NOTE: Date formatting uses hardcoded locale ('en-US')
 */

/**
 * WHAT:
 *   Formats duration in seconds to HH:MM:SS or MM:SS string.
 *
 * WHY:
 *   Provides readable time display.
 *
 * @param seconds
 *
 * @returns Formatted duration string (HH:MM:SS or MM:SS)
 *
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${String(hours)}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${String(minutes)}:${secs.toString().padStart(2, "0")}`;
};

export const formatTimeAgo = (date: string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${String(diffDays)} days ago`;
  }
  if (diffDays < 30) {
    return `${String(Math.floor(diffDays / 7))} weeks ago`;
  }
  return `${String(Math.floor(diffDays / 30))} months ago`;
};

export const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const formatTime = (date: string): string => {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const index = Math.floor(Math.log(bytes) / Math.log(k));
  return `${String(Number.parseFloat((bytes / Math.pow(k, index)).toFixed(2)))} ${sizes[index] ?? "B"}`;
};

export const formatPercentage = (value: number, total: number): string => {
  if (total === 0) {
    return "0%";
  }
  return `${String(Math.round((value / total) * 100))}%`;
};

export const formatSessionType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};
